import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { OpeningHoursDto } from '@workspace/data-types';

export interface DayScheduleDisplay {
  key: string;
  label: string;
  isOpen: boolean;
  isToday: boolean;
  statusText: string;
  slotsText: string;
}

@Component({
  selector: 'aura-opening-hours-display',
  imports: [MatIconModule],
  templateUrl: './opening-hours-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuraOpeningHoursDisplayComponent {
  readonly hours = input<OpeningHoursDto | undefined>(undefined);
  readonly title = input<string>("Horaires d'ouverture & permanence");
  readonly showTitle = input<boolean>(true);

  readonly weekSchedule = computed<DayScheduleDisplay[]>(() => {
    const hours = this.hours();
    const currentJsDay = new Date().getDay();

    const days: Array<{
      key: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
      label: string;
      jsDay: number;
    }> = [
      { key: 'monday', label: 'Lundi', jsDay: 1 },
      { key: 'tuesday', label: 'Mardi', jsDay: 2 },
      { key: 'wednesday', label: 'Mercredi', jsDay: 3 },
      { key: 'thursday', label: 'Jeudi', jsDay: 4 },
      { key: 'friday', label: 'Vendredi', jsDay: 5 },
      { key: 'saturday', label: 'Samedi', jsDay: 6 },
      { key: 'sunday', label: 'Dimanche', jsDay: 0 },
    ];

    return days.map((d) => {
      const dayData = hours?.[d.key];
      const isToday = currentJsDay === d.jsDay;

      if (!dayData || !dayData.isOpen) {
        return {
          key: d.key,
          label: d.label,
          isOpen: false,
          isToday,
          statusText: 'Fermé',
          slotsText: '',
        };
      }

      if (dayData.byAppointmentOnly) {
        return {
          key: d.key,
          label: d.label,
          isOpen: true,
          isToday,
          statusText: 'Sur rendez-vous uniquement',
          slotsText: '',
        };
      }

      const slots: string[] = [];
      if (dayData.morningOpen && dayData.morningClose) {
        slots.push(`${dayData.morningOpen} - ${dayData.morningClose}`);
      }
      if (dayData.afternoonOpen && dayData.afternoonClose) {
        slots.push(`${dayData.afternoonOpen} - ${dayData.afternoonClose}`);
      }

      if (slots.length === 0) {
        return {
          key: d.key,
          label: d.label,
          isOpen: false,
          isToday,
          statusText: 'Fermé',
          slotsText: '',
        };
      }

      const slotsText = slots.join(' & ');

      return {
        key: d.key,
        label: d.label,
        isOpen: true,
        isToday,
        statusText: slotsText,
        slotsText,
      };
    });
  });

  readonly openingHoursStatus = computed(() => {
    const schedule = this.weekSchedule();
    const today = schedule.find((d) => d.isToday);
    if (!today || !today.isOpen) {
      return {
        isOpen: false,
        text: 'Fermé aujourd’hui',
        badgeClass: 'bg-neutral-2 text-neutral-10 border-neutral-3',
      };
    }
    if (today.statusText === 'Sur rendez-vous uniquement') {
      return {
        isOpen: true,
        text: 'Sur rendez-vous aujourd’hui',
        badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
      };
    }
    return {
      isOpen: true,
      text: `Ouvert aujourd’hui (${today.slotsText})`,
      badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
    };
  });
}
